import api from '../../../services/api';
import {
  asArray,
  asBoolean,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from '../../../dto/common';

/**
 * ADMIN-only exit from DELIVERED. The backend owns every rule (blockers,
 * reason length, optimistic version); this module only normalizes the preview
 * read model and sends the command.
 */

export type OrderDeliveryRevertTarget = 'IN_DELIVERY' | 'READY_FOR_DISPATCH';

export interface OrderDeliveryRevertBlocker {
  code: 'ORDER_SETTLED_REVERT_BLOCKED' | 'ORDER_COMMERCIALLY_FROZEN' | string;
  settlements?: Array<{ id: string; kind: string; recognizedAt: string | null }>;
}

export interface OrderDeliveryRevertPreview {
  orderId: string;
  status: string;
  updatedAt: string;
  isDelivered: boolean;
  canRevert: boolean;
  targetStatuses: OrderDeliveryRevertTarget[];
  blockers: OrderDeliveryRevertBlocker[];
  releasedEarmarks: Array<{
    id: string;
    batchNumber: string | null;
    variantName: string | null;
    quantity: number;
    unit: string | null;
    releasedAt: string | null;
  }>;
  wzDocuments: Array<{ id: string; wzNumber: string | null; status: string }>;
  transports: Array<{ id: string; routeNumber: string | null; status: string }>;
  margin: {
    revenue: number;
    totalCost: number;
    margin: number;
    marginPercent: number;
    warehouseCostPending: boolean;
  };
}

const REVERT_TARGETS: OrderDeliveryRevertTarget[] = ['IN_DELIVERY', 'READY_FOR_DISPATCH'];

export function normalizeOrderDeliveryRevertPreview(raw: unknown, entity = 'orders.deliveryRevertPreview'): OrderDeliveryRevertPreview {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }
  const margin = asRecord(record?.margin);
  return {
    orderId: asString(record?.orderId),
    status: asString(record?.status),
    updatedAt: asString(record?.updatedAt),
    isDelivered: asBoolean(record?.isDelivered),
    canRevert: asBoolean(record?.canRevert),
    targetStatuses: asArray(record?.targetStatuses, (item) => asString(item))
      .filter((status): status is OrderDeliveryRevertTarget => REVERT_TARGETS.includes(status as OrderDeliveryRevertTarget)),
    blockers: asArray(record?.blockers, (item) => {
      const blocker = asRecord(item);
      return {
        code: asString(blocker?.code),
        settlements: asArray(blocker?.settlements, (settlement) => {
          const row = asRecord(settlement);
          return {
            id: asString(row?.id),
            kind: asString(row?.kind),
            recognizedAt: asOptionalString(row?.recognizedAt) ?? null,
          };
        }),
      };
    }),
    releasedEarmarks: asArray(record?.releasedEarmarks, (item) => {
      const row = asRecord(item);
      return {
        id: asString(row?.id),
        batchNumber: asOptionalString(row?.batchNumber) ?? null,
        variantName: asOptionalString(row?.variantName) ?? null,
        quantity: asNumber(row?.quantity),
        unit: asOptionalString(row?.unit) ?? null,
        releasedAt: asOptionalString(row?.releasedAt) ?? null,
      };
    }),
    wzDocuments: asArray(record?.wzDocuments, (item) => {
      const row = asRecord(item);
      return { id: asString(row?.id), wzNumber: asOptionalString(row?.wzNumber) ?? null, status: asString(row?.status) };
    }),
    transports: asArray(record?.transports, (item) => {
      const row = asRecord(item);
      return { id: asString(row?.id), routeNumber: asOptionalString(row?.routeNumber) ?? null, status: asString(row?.status) };
    }),
    margin: {
      revenue: asNumber(margin?.revenue),
      totalCost: asNumber(margin?.totalCost),
      margin: asNumber(margin?.margin),
      marginPercent: asNumber(margin?.marginPercent),
      warehouseCostPending: asBoolean(margin?.warehouseCostPending),
    },
  };
}

export async function fetchOrderDeliveryRevertPreview(orderId: string): Promise<OrderDeliveryRevertPreview> {
  const response = await api.get(`/orders/${orderId}/revert-delivery/preview`);
  return normalizeOrderDeliveryRevertPreview(response.data);
}

export interface OrderDeliveryRevertPayload {
  targetStatus: OrderDeliveryRevertTarget;
  reason: string;
  expectedUpdatedAt: string;
}

export async function revertOrderDelivery(orderId: string, payload: OrderDeliveryRevertPayload): Promise<void> {
  await api.post(`/orders/${orderId}/revert-delivery`, payload);
}

/** Stable backend codes → Polish copy keys (orders namespace). */
export const ORDER_DELIVERY_REVERT_ERROR_KEYS: Record<string, string> = {
  ORDER_SETTLED_REVERT_BLOCKED: 'orders:deliveryRevert.errors.settled',
  ORDER_COMMERCIALLY_FROZEN: 'orders:deliveryRevert.errors.frozen',
  ORDER_NOT_DELIVERED: 'orders:deliveryRevert.errors.notDelivered',
  VERSION_CONFLICT: 'orders:deliveryRevert.errors.versionConflict',
  REVERT_REASON_TOO_SHORT: 'orders:deliveryRevert.errors.reasonTooShort',
};
